<template>
  <ul v-if="variant === 'list'" class="example-sentence-list">
    <li class="example-sentence-item">
      <p class="question-title">質問1.あなたの長所と短所 <span>必須</span></p>
      <p class="question-title">長所</p>
      <div :class="['el-textarea', { mb10: goodMb10 }]">
        <el-input
          type="textarea"
          :autosize="resolved.goodAutosize"
          :placeholder="resolved.goodPlaceholder"
          title="長所"
          autocomplete="off"
          :value="good"
          @input="emit('update:good', $event)"
        />
      </div>
      <p class="question-title">短所</p>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.badAutosize"
          :placeholder="resolved.badPlaceholder"
          title="短所"
          autocomplete="off"
          :value="bad"
          @input="emit('update:bad', $event)"
        />
      </div>
    </li>
    <li class="example-sentence-item">
      <p class="question-title">質問2.あなたの趣味</p>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.hobbyAutosize"
          :placeholder="resolved.hobbyPlaceholder"
          title="あなたの趣味"
          autocomplete="off"
          :value="hobby"
          @input="emit('update:hobby', $event)"
        />
      </div>
    </li>
    <li class="example-sentence-item">
      <p class="question-title">質問3.今後やりたいこと</p>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.wantedAutosize"
          :placeholder="resolved.wantedPlaceholder"
          title="今後やりたいこと"
          autocomplete="off"
          :value="wanted"
          @input="emit('update:wanted', $event)"
        />
      </div>
    </li>
    <li class="example-sentence-item">
      <p class="question-title">質問4.その他アピールしたいこと</p>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.appealAutosize"
          :placeholder="resolved.appealPlaceholder"
          title="その他アピールしたいこと"
          autocomplete="off"
          :value="appeal"
          @input="emit('update:appeal', $event)"
        />
      </div>
    </li>
  </ul>
  <ul v-else class="example-sentence">
    <li>
      <div class="title">Q.1 あなたの長所と短所</div>
      <div class="el-textarea">
        <div class="flex" style="margin-top: 10px; align-items: center">
          <span style="width: 10%; font-size: 0.8rem">長所</span>
          <el-input
            type="textarea"
            :autosize="resolved.goodAutosize"
            :placeholder="resolved.goodPlaceholder"
            title="長所"
            autocomplete="off"
            :value="good"
            @input="emit('update:good', $event)"
          />
        </div>
        <div class="flex" style="margin-top: 10px; align-items: center">
          <span style="width: 10%; font-size: 0.8rem">短所</span>
          <el-input
            type="textarea"
            :autosize="resolved.badAutosize"
            :placeholder="resolved.badPlaceholder"
            title="短所"
            autocomplete="off"
            :value="bad"
            @input="emit('update:bad', $event)"
          />
        </div>
      </div>
    </li>
    <li>
      <div class="title">Q.2 あなたの趣味</div>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.hobbyAutosize"
          :placeholder="resolved.hobbyPlaceholder"
          title="あなたの趣味"
          autocomplete="off"
          :value="hobby"
          @input="emit('update:hobby', $event)"
        />
      </div>
    </li>
    <li>
      <div class="title">Q.3 今後やりたいこと</div>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.wantedAutosize"
          :placeholder="resolved.wantedPlaceholder"
          title="今後やりたいこと"
          autocomplete="off"
          :value="wanted"
          @input="emit('update:wanted', $event)"
        />
      </div>
    </li>
    <li>
      <div class="title">Q.4 その他アピールしたいこと</div>
      <div class="el-textarea">
        <el-input
          type="textarea"
          :autosize="resolved.appealAutosize"
          :placeholder="resolved.appealPlaceholder"
          title="その他アピールしたいこと"
          autocomplete="off"
          :value="appeal"
          @input="emit('update:appeal', $event)"
        />
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
/**
 * 書式 AI mypr の 4 質問フォーム（list / qa の 2 レイアウト）。
 */
import { computed } from 'vue'

type AiMyprAutosize = boolean | { minRows?: number; maxRows?: number }

const props = withDefaults(
  defineProps<{
    variant?: 'list' | 'qa'
    good: string
    bad: string
    hobby: string
    wanted: string
    appeal: string
    goodPlaceholder?: string
    badPlaceholder?: string
    hobbyPlaceholder?: string
    wantedPlaceholder?: string
    appealPlaceholder?: string
    goodAutosize?: AiMyprAutosize
    badAutosize?: AiMyprAutosize
    hobbyAutosize?: AiMyprAutosize
    wantedAutosize?: AiMyprAutosize
    appealAutosize?: AiMyprAutosize
    goodMb10?: boolean
  }>(),
  {
    variant: 'list',
    goodMb10: true
  }
)

const emit = defineEmits<{
  (e: 'update:good', value: string): void
  (e: 'update:bad', value: string): void
  (e: 'update:hobby', value: string): void
  (e: 'update:wanted', value: string): void
  (e: 'update:appeal', value: string): void
}>()

const listDefaults = {
  goodPlaceholder: '丁寧に取り組むことができるなど',
  badPlaceholder: 'スケジュールがギリギリなど',
  hobbyPlaceholder: 'ヤギを数えること',
  wantedPlaceholder: 'ヤギを飼うこと',
  appealPlaceholder: 'ヤギに好かれること',
  goodAutosize: { minRows: 2, maxRows: 5 } as AiMyprAutosize,
  badAutosize: { minRows: 2, maxRows: 5 } as AiMyprAutosize,
  hobbyAutosize: { minRows: 3, maxRows: 5 } as AiMyprAutosize,
  wantedAutosize: { minRows: 3, maxRows: 10 } as AiMyprAutosize,
  appealAutosize: { minRows: 3, maxRows: 10 } as AiMyprAutosize
}

const qaDefaults = {
  goodPlaceholder: '',
  badPlaceholder: '',
  hobbyPlaceholder: '',
  wantedPlaceholder: '',
  appealPlaceholder: '',
  goodAutosize: true as AiMyprAutosize,
  badAutosize: true as AiMyprAutosize,
  hobbyAutosize: true as AiMyprAutosize,
  wantedAutosize: { minRows: 3, maxRows: 15 } as AiMyprAutosize,
  appealAutosize: { minRows: 3, maxRows: 15 } as AiMyprAutosize
}

const resolved = computed(() => {
  const base = props.variant === 'qa' ? qaDefaults : listDefaults
  return {
    goodPlaceholder: props.goodPlaceholder ?? base.goodPlaceholder,
    badPlaceholder: props.badPlaceholder ?? base.badPlaceholder,
    hobbyPlaceholder: props.hobbyPlaceholder ?? base.hobbyPlaceholder,
    wantedPlaceholder: props.wantedPlaceholder ?? base.wantedPlaceholder,
    appealPlaceholder: props.appealPlaceholder ?? base.appealPlaceholder,
    goodAutosize: props.goodAutosize ?? base.goodAutosize,
    badAutosize: props.badAutosize ?? base.badAutosize,
    hobbyAutosize: props.hobbyAutosize ?? base.hobbyAutosize,
    wantedAutosize: props.wantedAutosize ?? base.wantedAutosize,
    appealAutosize: props.appealAutosize ?? base.appealAutosize
  }
})
</script>
